import React, { useState } from 'react';
import { Box, Typography, Divider, Tabs, Tab, IconButton } from '@mui/material';
import {
  KeyboardArrowRight as ChevronRightIcon,
  KeyboardArrowDown as ChevronDownIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import StatusChip from '../../../components/StatusChip';
import { formatEventTimestamp } from '../../../utils/dateUtils';
import { getStatusColor } from '../../../utils/statusChipUtil';
import CodeSnippetRenderer from '../../../components/CodeSnippetRenderer';

/**
 * DeliveryAttemptsList component displays chronological list of delivery attempts
 * @param {Array} attempts - Array of delivery attempt objects
 */
const DeliveryAttemptsList = ({ attempts }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [expandedAttempts, setExpandedAttempts] = useState(new Set());

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const toggleAttempt = (attemptId) => {
    setExpandedAttempts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(attemptId)) {
        newSet.delete(attemptId);
      } else {
        newSet.add(attemptId);
      }
      return newSet;
    });
  };

  // Filter attempts based on selected tab (for now, only "All" is supported)
  const filteredAttempts = attempts || [];

  if (filteredAttempts.length === 0) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 0.5 }}>
          Deliveries to webhook endpoints
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 2 }}>
          Attempts to send this event to your webhook endpoints in the past 15 days.
        </Typography>
        <Box sx={{ py: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No delivery attempts yet
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      {/* Heading and Subtitle */}
      <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 0.5 }}>
        Deliveries to webhook endpoints
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 2 }}>
        Attempts to send this event to your webhook endpoints in the past 15 days.
      </Typography>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={selectedTab} onChange={handleTabChange} sx={{ minHeight: 'auto' }}>
          <Tab
            label="All"
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              minHeight: '36px',
              py: 1,
            }}
          />
          <Tab
            label="Succeeded"
            disabled
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              minHeight: '36px',
              py: 1,
            }}
          />
          <Tab
            label="Failed"
            disabled
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              minHeight: '36px',
              py: 1,
            }}
          />
        </Tabs>
      </Box>

      {/* Delivery Attempts List */}
      <Box>
        {filteredAttempts.map((attempt, index) => {
          // Use http_status from API response
          const httpStatus = attempt.http_status;
          const statusColor = getStatusColor(httpStatus);
          const statusLabel = httpStatus ? String(httpStatus) : '—';

          // Get webhook URL (not in response, might come from event details or webhook config)
          const webhookUrl = attempt.webhook_url || attempt.endpoint_url || attempt.url || '';

          // Get latency in milliseconds
          const latencyMs = attempt.latency_ms;

          // Get finished at timestamp
          const finishedAt = attempt.finished_at;

          // Get unique ID for this attempt
          const attemptId = attempt.attempt_no || attempt.id || index;
          const isExpanded = expandedAttempts.has(attemptId);

          // Get request and response payloads
          const requestPayload = attempt.request_payload || attempt.requestPayload;
          const responsePayload = attempt.response_payload || attempt.responsePayload;

          const handleRowClick = () => {
            toggleAttempt(attemptId);
          };

          const handleKeyDown = (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleRowClick();
            }
          };

          const handleButtonClick = (e) => {
            e.stopPropagation();
          };

          return (
            <React.Fragment key={attemptId}>
              <Box>
                {/* Header Row */}
                <Box
                  onClick={handleRowClick}
                  onKeyDown={handleKeyDown}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1.25,
                    px: 1.5,
                    cursor: 'pointer',
                    borderRadius: '6px',
                    border: '2px solid transparent',
                    backgroundColor: 'transparent',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    '&:focus': {
                      outline: 'none',
                    },
                  }}
                  tabIndex={0}
                >
                  {/* Left: Chevron, Status Chip, URL */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                      {isExpanded ? <ChevronDownIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                    </Box>
                    <StatusChip color={statusColor} label={statusLabel} size="small" />
                    {webhookUrl && (
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{
                          fontSize: '0.875rem',
                          fontFamily: 'monospace',
                          maxWidth: '400px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {webhookUrl}
                      </Typography>
                    )}
                  </Box>

                  {/* Right: Timestamp, Menu */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: '0.875rem', minWidth: '160px', textAlign: 'right' }}
                    >
                      {finishedAt ? formatEventTimestamp(finishedAt) : '—'}
                    </Typography>
                  </Box>
                </Box>

                {/* Expanded Content: Request and Response Payloads */}
                {isExpanded && (
                  <Box sx={{ pl: 4, pr: 0, py: 2 }}>
                    <CodeSnippetRenderer title="Request Payload" payload={requestPayload} />
                    <CodeSnippetRenderer title="Response Payload" payload={responsePayload} />
                  </Box>
                )}
              </Box>
              {index < filteredAttempts.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

export default DeliveryAttemptsList;
